/** @jsxImportSource preact */
import type { Ref } from "preact";

import * as m from "../paraglide/messages.js";
import type { VoiceStatus } from "../voice/controller";
import { MicIcon, MicOffIcon, SendIcon, SpinnerIcon } from "./icons";
import { SR_ONLY } from "./sr-only";
import { SERIF_META_FONT, type WidgetTheme } from "./theme";

export const COMPOSER_STYLES = `
.gn-composer-input { font:inherit; padding-block:10px; padding-block:max(4px, calc((var(--gn-composer-height) - 1lh) / 2)); }
.gn-composer-input::placeholder { color:var(--gn-placeholder); font-style:var(--gn-placeholder-style); opacity:1; }
/* iOS 聚焦小于 16px 的输入框会缩放页面；只在触屏上抬字号，桌面保持正文字号。
   字体不要写回行内样式：行内的 font 简写会把这条字号盖掉。 */
@media (pointer:coarse) { .gn-composer-input { font-size:max(16px, 1em); } }
/* 按抽屉所在档位的主体高度（抽屉写入的 --gn-drawer-body-height）连续收紧留白，180px 以下保留紧凑布局，340px 以上恢复正常留白。
   这个高度不含手指拖动的位移：拖动时输入区不变，松手后随档位动画一起变。
   档位动画中这两个值每帧都变，要取整：带小数会让消息区的整像素贴底补偿对不齐，气泡跟着抖；@supports 外是不支持 round() 时的退路。 */
.gn-drawer-composer { max-height:max(calc(1lh + 10px), calc(var(--gn-drawer-body-height) * .25)); overflow-y:auto !important; }
.gn-drawer-composer-form { padding-block:0 clamp(4px, calc(var(--gn-drawer-body-height) * .05 - 5px), 12px) !important; }
@supports (height: round(1.5px, 1px)) {
  .gn-drawer-composer { max-height:max(calc(1lh + 10px), round(calc(var(--gn-drawer-body-height) * .25), 1px)); }
  .gn-drawer-composer-form { padding-block:0 clamp(4px, round(calc(var(--gn-drawer-body-height) * .05 - 5px), 1px), 12px) !important; }
}
/* iOS 把光标挂在最近的合成层上（毛玻璃面板就是一层），只在选区变化时重算光标在层里的位置。
   抽屉升档、拖动时输入框在面板里挪了位置、选区没变，光标就留在旧处；输入框自己成层，光标才跟着它走。 */
.gn-drawer-composer { will-change:transform; }
.gn-composer-frame:focus-within { border-color:var(--gn-composer-focus) !important; }
`;

export interface ComposerProps {
  theme: WidgetTheme;
  draft: string;
  onDraftChange: (draft: string) => void;
  /** 回车或点发送键；发不发由调用方按 canSend 决定。 */
  onSubmit: () => void;
  canSend: boolean;
  busy: boolean;
  placeholder: string;
  /** 输入框自动长高由 useComposerSize 管，它要拿到这个 ref。 */
  inputRef?: Ref<HTMLTextAreaElement>;
  /** 手机上回车换行不发送。 */
  mobile?: boolean;
  /** 在移动抽屉里：输入区随抽屉档位收紧留白。 */
  drawer?: boolean;
  /** 刚发出这一句：发送键弹一下。 */
  justSent?: boolean;
  poweredBy?: boolean;
  voiceEnabled?: boolean;
  voiceStatus?: VoiceStatus;
  voiceError?: string;
  onVoiceToggle?: () => void;
}

/** 输入区：多行输入框、浮在右下角的发送键，以及页脚那行 Powered by。 */
export function Composer({
  theme,
  draft,
  onDraftChange,
  onSubmit,
  canSend,
  busy,
  placeholder,
  inputRef,
  mobile = false,
  drawer = false,
  justSent = false,
  poweredBy = true,
  voiceEnabled,
  voiceStatus,
  voiceError,
  onVoiceToggle,
}: ComposerProps) {
  // 忙的时候按钮照样是强调色，只是换成转圈：变灰跟「一个字都没打」长一个样，
  // 买家按完看不出到底发出去没有。
  const litButton = busy || canSend;
  const voiceLabel =
    voiceStatus === "connecting"
      ? m.widget_voice_connecting()
      : voiceStatus === "listening"
        ? m.widget_voice_listening()
        : voiceStatus === "answering"
          ? m.widget_voice_answering()
          : voiceStatus === "error"
            ? (voiceError ?? m.widget_voice_error())
            : m.widget_voice_enable();
  const voiceOn =
    voiceEnabled ??
    (voiceStatus !== undefined &&
      voiceStatus !== "off" &&
      voiceStatus !== "error");
  const hasVoiceToggle =
    onVoiceToggle !== undefined && voiceStatus !== undefined;
  return (
    <div
      data-base-ui-swipe-ignore={drawer ? "" : undefined}
      style={{
        paddingBottom: drawer
          ? "var(--gn-composer-bottom-inset, env(safe-area-inset-bottom, 0px))"
          : undefined,
        flex: "none",
        position: "relative",
      }}
    >
      {/* 处理中由会话末行展示，这里不写第二遍给眼睛看。
          live region 要常驻、到时候才填字，整块条件渲染读屏播不出来。 */}
      <span role="status" style={SR_ONLY}>
        {busy ? m.widget_composer_busy() : ""}
      </span>
      <form
        class={drawer ? "gn-drawer-composer-form" : undefined}
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
        // Editorial 不是「框里套框」，输入区只留一条上边线。
        style={
          theme.editorial
            ? {
                borderTop: `1px solid ${theme.divider}`,
                padding: "10px 12px 10px 18px",
              }
            : { padding: "0 12px 6px" }
        }
      >
        <div
          class={theme.editorial ? undefined : "gn-composer-frame"}
          style={{
            "--gn-composer-height": theme.editorial ? "32px" : "42px",
            "--gn-composer-focus": theme.accentFill,
            boxSizing: "border-box",
            position: "relative",
            ...(theme.editorial
              ? {}
              : {
                  background: theme.field,
                  border: `1px solid ${theme.line}`,
                  borderRadius: theme.ctrlRadius,
                }),
          }}
        >
          <textarea
            // 无障碍名称固定用商家配的 placeholder：忙不忙是 role="status"
            // 那条的事，控件自己的名字不该跟着状态变。
            aria-label={placeholder}
            class={
              drawer
                ? "gn-composer-input gn-drawer-composer"
                : "gn-composer-input"
            }
            ref={inputRef}
            rows={1}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" &&
                !event.shiftKey &&
                !mobile &&
                !event.isComposing &&
                // Safari 的输入法确认可能先结束 composition，再派发 Enter。
                // eslint-disable-next-line @typescript-eslint/no-deprecated
                event.keyCode !== 229
              ) {
                event.preventDefault();
                onSubmit();
              }
            }}
            onInput={(event) => {
              onDraftChange(event.currentTarget.value);
            }}
            // 等回复时也让买家接着打下一句——send() 的错误分支本来就写着
            // 不覆盖买家新输入的字，原来的 readOnly 跟它是打架的。
            placeholder={placeholder}
            style={{
              overscrollBehavior: "contain",
              scrollbarColor: `${theme.line} transparent`,
              scrollbarWidth: "thin",
              background: "transparent",
              border: 0,
              boxSizing: "border-box",
              color: theme.ink,
              display: "block",
              width: "100%",
              lineHeight: 1.5,
              minWidth: 0,
              outline: "none",
              paddingInline: theme.editorial
                ? hasVoiceToggle
                  ? "0 84px"
                  : "0 44px"
                : hasVoiceToggle
                  ? "14px 92px"
                  : "14px 50px",
              resize: "none",
            }}
            value={draft}
          />
          {onVoiceToggle !== undefined && voiceStatus !== undefined && (
            <button
              type="button"
              aria-label={
                voiceOn ? m.widget_voice_disable() : m.widget_voice_enable()
              }
              data-testid="voice-toggle"
              data-voice-toggle="true"
              data-voice-state={voiceStatus}
              onClick={onVoiceToggle}
              style={{
                background: voiceOn ? theme.accentSoft : "transparent",
                border: 0,
                borderRadius: theme.sendRadius,
                color: voiceOn ? theme.accentFill : theme.muted,
                cursor: "pointer",
                display: "grid",
                height: 36,
                placeItems: "center",
                padding: 0,
                position: "absolute",
                right: 42,
                top: 3,
                width: 36,
              }}
            >
              {voiceOn ? <MicIcon size={16} /> : <MicOffIcon size={16} />}
            </button>
          )}
          {/* 发送键浮在输入框右下角，保留 44px 点击区（GON-82）；
              右侧为文字留白；按钮点击区与外缘滚动条之间保持间距。 */}
          <button
            aria-label={m.widget_composer_send()}
            class="gn-send"
            data-sent={justSent || undefined}
            disabled={!canSend}
            style={{
              background: "transparent",
              border: 0,
              cursor: canSend ? "pointer" : "default",
              display: "grid",
              position: "absolute",
              // 可见 32px 键距输入框外缘 6px；Editorial 没有框，贴着行尾。
              bottom: theme.editorial ? -6 : -1,
              right: theme.editorial ? -6 : -1,
              height: 44,
              padding: 0,
              placeItems: "center",
              // iOS 点按时会给整个按钮盒子罩一层灰，透明的那圈会闪出一个方块。
              WebkitTapHighlightColor: "transparent",
              width: 44,
            }}
            type="submit"
          >
            {/* 空态不再把主色压半透明：黑色主色会变成一块中灰，和米色气泡、卡片托盘都不搭。 */}
            <span
              style={{
                background: litButton ? theme.accentFill : theme.trayBg,
                borderRadius: theme.sendRadius,
                color: litButton ? theme.accentFillFg : theme.muted,
                display: "grid",
                height: 32,
                placeItems: "center",
                width: 32,
              }}
            >
              {busy ? <SpinnerIcon size={16} /> : <SendIcon size={16} />}
            </span>
          </button>
        </div>
        {onVoiceToggle !== undefined &&
          voiceStatus !== undefined &&
          voiceStatus !== "off" && (
            <span
              role="status"
              aria-live="polite"
              data-voice-state={voiceStatus}
              style={{
                color: voiceStatus === "error" ? theme.dangerText : theme.muted,
                display: "block",
                fontSize: 11,
                lineHeight: 1.3,
                minHeight: 15,
                padding: "3px 12px 0",
              }}
            >
              {voiceLabel}
            </span>
          )}
      </form>
      {/* 标题栏归商家，我们只在页脚留一行小字。 */}
      {poweredBy && (
        <div
          style={{
            color: theme.faint,
            fontFamily: theme.serif ? SERIF_META_FONT : undefined,
            fontSize: 11,
            padding: "0 0 10px",
            textAlign: "center",
          }}
        >
          {m.widget_footer_powered_by()}
        </div>
      )}
    </div>
  );
}
